<template>
  <div class="add-laboratory-act" style="width: 560px; max-width: 100%;">
    <q-form @submit.prevent="onSubmit" class="form">
      <div class="add-laboratory-act__section add-laboratory-act__section--lab">
        <div class="add-laboratory-act__section-title">Лабораторный акт</div>
        <div class="form__item">
          <CustomInput
            label="Название"
            placeholder="Введите название"
            :required="true"
            :value="formData.laboratory_act_name || ''"
            @change_input="(v) => onChange('laboratory_act_name', v)"
          />
        </div>
        <div v-if="!isEdit" class="form__item">
          <CustomUploader
            :value="formData.file"
            :on-change="handleFileSelect"
            title="Файл акта"
          />
        </div>
        <div v-else class="form__item">
          <p class="tw-text-xs tw-text-grey-7 tw-mb-1">
            Новый файл не обязателен — при выборе заменит текущий.
          </p>
          <CustomUploader
            :value="formData.file"
            :on-change="handleFileSelect"
            title="Заменить файл (необязательно)"
          />
        </div>
      </div>

      <div
        v-if="!isEdit"
        class="add-laboratory-act__section add-laboratory-act__section--context"
      >
        <div class="add-laboratory-act__section-title">Этаж и работы</div>
        <div class="form__item">
          <CustomSelect
            label="Этаж"
            :options="floorOptions"
            optionValueKey="value"
            optionLabelKey="label"
            :value="selectedFloorMapId"
            :loading="floorsLoading"
            :disabled="!floorOptions.length"
            :required="true"
            :full-width="true"
            @change="onFloorChange"
          />
        </div>
        <div class="form__item">
          <CustomSelect
            label="Тип помещения"
            :options="placementOptions"
            optionValueKey="value"
            optionLabelKey="label"
            :value="selectedPlacementTypeId"
            :loading="workSetsLoading"
            :disabled="!selectedFloorMapId"
            :required="true"
            :full-width="true"
            @change="onPlacementChange"
          />
        </div>
        <div class="form__item">
          <CustomSelect
            label="Группа работ"
            :options="workGroupOptions"
            optionValueKey="value"
            optionLabelKey="label"
            :value="selectedWorkSetCheckGroupId"
            :disabled="!selectedPlacementTypeId"
            :required="true"
            :full-width="true"
            @change="(v) => (selectedWorkSetCheckGroupId = v)"
          />
        </div>
      </div>

      <CustomModalFooter :btn-loading="loading" />
    </q-form>
  </div>
</template>

<script>
import CustomInput from "@/components/form/CustomInput";
import CustomSelect from "@/components/form/CustomSelect";
import CustomModalFooter from "@/components/modal/modals/modal-footer";
import CustomUploader from "@/components/form/CustomUploader";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { useAppStore } from "@/services/store/app";
import {
  getEntranceApartments,
  getFloorWorkSets,
  createProjectLaboratoryAct,
  updateProjectLaboratoryAct,
} from "@/views/ResidentialSettings/services";

export default {
  name: "add-laboratory-act",
  components: {
    CustomInput,
    CustomSelect,
    CustomModalFooter,
    CustomUploader,
  },
  props: ["data"],
  data() {
    const act = this.data?.act || {};
    return {
      loading: false,
      projectId: this.data?.projectId,
      formData: {
        project_laboratory_act_id: act.project_laboratory_act_id || null,
        laboratory_act_name: act.laboratory_act_name || "",
        file: null,
      },
      floorsLoading: false,
      workSetsLoading: false,
      floorOptions: [],
      workSetTypes: [],
      selectedFloorMapId: null,
      selectedPlacementTypeId: null,
      selectedWorkSetCheckGroupId: null,
    };
  },
  computed: {
    isEdit() {
      return !!this.formData.project_laboratory_act_id;
    },
    placementOptions() {
      if (!this.workSetTypes?.length) return [];
      return this.workSetTypes.map((t) => ({
        value: t.placement_type_id,
        label: t.placement_type_name || `Тип ${t.placement_type_id}`,
      }));
    },
    workGroupOptions() {
      if (this.selectedPlacementTypeId == null) return [];
      const type = this.workSetTypes.find(
        (t) => t.placement_type_id === this.selectedPlacementTypeId
      );
      const groups = type?.work_set_check_groups || [];
      return groups.map((g) => ({
        value: g.work_set_check_group_id,
        label: g.work_set_check_group_name || `Группа ${g.work_set_check_group_id}`,
      }));
    },
  },
  async mounted() {
    await this.loadFloors();
  },
  methods: {
    onChange(key, value) {
      this.formData[key] = value;
    },
    handleFileSelect(file) {
      if (!file?.length) return;
      this.formData.file = file;
    },
    onFloorChange(v) {
      this.selectedFloorMapId = v;
      this.selectedPlacementTypeId = null;
      this.selectedWorkSetCheckGroupId = null;
      this.workSetTypes = [];
      if (v) {
        this.loadWorkSetsForFloor(v);
      }
    },
    onPlacementChange(v) {
      this.selectedPlacementTypeId = v;
      this.selectedWorkSetCheckGroupId = null;
    },
    async loadFloors() {
      const params = this.data?.params;
      if (!params?.project_entrance_id && !params?.project_id) {
        this.floorOptions = [];
        return;
      }
      this.floorsLoading = true;
      const res = await getEntranceApartments(params);
      this.floorsLoading = false;
      const list = res?.data || [];
      this.floorOptions = list.map((row) => ({
        value: row.floor_map_id,
        label: `Этаж ${row.floor ?? ""}`.trim(),
        raw: row,
      }));
    },
    async loadWorkSetsForFloor(floorMapId) {
      this.workSetsLoading = true;
      const res = await getFloorWorkSets(floorMapId);
      this.workSetsLoading = false;
      const rows = res?.data != null ? res.data : Array.isArray(res) ? res : [];
      this.workSetTypes = Array.isArray(rows) ? rows : [];
    },
    async onSubmit() {
      if (!String(this.formData.laboratory_act_name || "").trim()) {
        this.showErrorSnackbar("Введите название лабораторного акта");
        return;
      }
      if (!this.isEdit && !this.formData.file?.length) {
        this.showErrorSnackbar("Выберите файл лабораторного акта");
        return;
      }
      if (!this.projectId) return;

      if (!this.isEdit) {
        if (!this.selectedFloorMapId) {
          this.showErrorSnackbar("Выберите этаж");
          return;
        }
        if (this.selectedPlacementTypeId == null) {
          this.showErrorSnackbar("Выберите тип помещения");
          return;
        }
        if (this.selectedWorkSetCheckGroupId == null) {
          this.showErrorSnackbar("Выберите группу работ");
          return;
        }
      }

      const nameTrimmed = String(this.formData.laboratory_act_name || "").trim();

      let res;
      this.loading = true;
      if (this.isEdit) {
        const body = {
          project_laboratory_act_id: this.formData.project_laboratory_act_id,
          laboratory_act_name: nameTrimmed,
        };
        if (this.formData.file?.length) {
          body.file = this.formData.file[0];
        }
        res = await updateProjectLaboratoryAct(this.projectId, body);
      } else {
        const body = {
          laboratory_act_name: nameTrimmed,
          file: this.formData.file[0],
          floor_map_id: this.selectedFloorMapId,
          placement_type_id: this.selectedPlacementTypeId,
          work_set_check_group_id: this.selectedWorkSetCheckGroupId,
        };
        res = await createProjectLaboratoryAct(this.projectId, body);
      }
      this.loading = false;
      if (!res) return;

      if (this.data?.onSubmit) {
        await this.data.onSubmit(res);
      }

      if (!this.isEdit) {
        this.showSucceedSnackbar("Лабораторный акт создан");
      } else {
        this.showSucceedSnackbar();
      }

      this.closeModal();
    },
  },
  setup() {
    const { closeModal } = useAppStore();
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar, showErrorSnackbar, closeModal };
  },
};
</script>

<style scoped lang="scss">
.add-laboratory-act {
  &__section {
    padding: 12px 14px;
    border-radius: 8px;
    margin-bottom: 16px;

    &--lab {
      background: #f0f9ff;
      border: 1px solid #bae6fd;
    }

    &--context {
      border: 1px solid #d6d3d1;
    }
  }

  &__section-title {
    font-size: 15px;
    font-weight: 600;
    color: #0c4a6e;
    margin-bottom: 10px;
  }

  &--context .add-laboratory-act__section-title {
    color: #44403c;
  }
}

.form {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 0;
  margin-top: 4px;

  &__item {
    margin-bottom: 12px;
  }
}
</style>
